/**
 * Особистий шар подання (views.md, розд. 5; тікет 8ae40447, E4a-03): вибір і порядок стовпців
 * (LST-02), ширини (LST-03) і закріплення в перемикачі (FLT-06, E4a-W3) — на сервері,
 * `PUT /api/v1/views/{id}/prefs`, на всіх пристроях людини. Сервер віддає шар у `/meta`
 * (`view.prefs`) лише з видимими полями. Стовпці — без поля-назви, яке завжди перше.
 *
 * Перехід із localStorage: старий вибір (`prefs.listColumns`, W2) переноситься на сервер одноразово,
 * при першому відкритті подання без серверного шару, і з пристрою прибирається (коментар PM 20.10).
 * Нових записів у localStorage немає.
 */
import type { AppMeta, MetaView } from '@clientline/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { getPrefs, LIST_COLUMNS_MAX, setPrefs } from '../prefs/prefs.ts';
import { api, platformKeys } from './api.ts';
import { personalColumns, withPersonalColumns } from './list-source.tsx';

type ViewPrefs = NonNullable<MetaView['prefs']>;

/** Тіло `PUT …/prefs`: шар цілком (сервер замінює рядок). */
function bodyOf(prefs: ViewPrefs) {
  return {
    pinned: prefs.pinned === true,
    position: prefs.position ?? null,
    columns: prefs.columns ? [...prefs.columns] : null,
    widths: prefs.widths ?? null,
    collapsed: prefs.collapsed ? [...prefs.collapsed] : null,
  };
}

const isEmpty = (body: ReturnType<typeof bodyOf>) =>
  !body.pinned &&
  body.position === null &&
  body.columns === null &&
  body.widths === null &&
  body.collapsed === null;

/** Шар подання в кеші `/meta` — одразу, до відповіді сервера (екран не чекає мережі). */
function patchMeta(client: QueryClient, viewId: string, prefs: ViewPrefs | undefined): void {
  client.setQueryData<AppMeta>(platformKeys.meta, (meta) =>
    meta
      ? {
          ...meta,
          views: meta.views.map((v) => {
            if (v.id !== viewId) return v;
            const { prefs: _old, ...rest } = v;
            return prefs ? { ...rest, prefs } : rest;
          }),
        }
      : meta,
  );
}

/**
 * Шар подання з кешу `/meta`: на момент запису — найсвіжіший (і після відкладених змін).
 * `undefined` — подання в кеші немає (кеш ще не прийшов чи вже очищений).
 */
function currentPrefs(client: QueryClient, viewId: string): ViewPrefs | undefined {
  const view = client.getQueryData<AppMeta>(platformKeys.meta)?.views.find((v) => v.id === viewId);
  return view ? (view.prefs ?? {}) : undefined;
}

/**
 * Записати шар подання з кешу `/meta` на сервер: на момент відправки, тож відкладена ширина не
 * затре новіших стовпців. Подання в кеші немає — знімок `snapshot` зміни. Збій — кеш `/meta`
 * перечитується, щоб екран показав правду сервера.
 */
function persist(client: QueryClient, id: string, snapshot: ViewPrefs): Promise<void> {
  return inOrder(id, async () => {
    const body = bodyOf(currentPrefs(client, id) ?? snapshot);
    try {
      if (isEmpty(body)) await api('DELETE', `/api/v1/views/${id}/prefs`);
      else await api('PUT', `/api/v1/views/${id}/prefs`, { body });
    } catch (error) {
      void client.invalidateQueries({ queryKey: platformKeys.meta });
      throw error;
    }
  });
}

/** Змінити шар подання в кеші `/meta` (одразу); подання без id (старий сервер) шару не має. */
function change(
  client: QueryClient,
  view: MetaView,
  edit: (prefs: ViewPrefs) => void,
): { id: string; next: ViewPrefs } {
  const id = view.id;
  if (!id) throw new Error('подання без id');
  const next: ViewPrefs = { ...(currentPrefs(client, id) ?? view.prefs ?? {}) };
  edit(next);
  patchMeta(client, id, isEmpty(bodyOf(next)) ? undefined : next);
  return { id, next };
}

/**
 * Межа згорнутих стовпців у шарі — `VIEW_LIMITS.collapsed` контракту (тест звіряє). Своя стала: zod
 * контракту в перший екран не тягнемо.
 */
export const COLLAPSED_MAX = 50;

/**
 * Згорнуті стовпці дошки (KAN-03): ключі етапів у шарі людини, на всіх її пристроях. Список —
 * повний набір: порожній — «нічого не згорнуто», зокрема етапи, які компанія згортає за
 * замовчуванням (`board.collapsed` подання); `null` — повернутися до значень подання. Решта шару
 * лишається.
 */
export async function saveViewCollapsed(
  client: QueryClient,
  view: MetaView,
  collapsed: readonly string[] | null,
): Promise<void> {
  const { id, next } = change(client, view, (prefs) => {
    if (collapsed === null) delete prefs.collapsed;
    // Понад межу сервера найстаріші ключі відкидаються.
    else prefs.collapsed = [...new Set(collapsed)].slice(-COLLAPSED_MAX);
  });
  await persist(client, id, next);
}

/**
 * Зберегти особистий вибір стовпців подання; `undefined` — «як у поданні». Решта шару (закріплення,
 * ширини) лишається.
 */
export async function saveViewColumns(
  client: QueryClient,
  view: MetaView,
  columns: readonly string[] | undefined,
): Promise<void> {
  const { id, next } = change(client, view, (prefs) => {
    if (columns === undefined) delete prefs.columns;
    else prefs.columns = [...columns];
  });
  await persist(client, id, next);
}

/**
 * Пауза після останнього жесту ширини перед записом (views.md, розд. 5): тягнути межу чи тиснути
 * стрілки — одна зміна шару, а не запит на кожен крок.
 */
export const WIDTHS_SAVE_DELAY_MS = 1000;
const widthTimers = new Map<string, { timer: ReturnType<typeof setTimeout>; next: ViewPrefs }>();

/**
 * Особиста ширина стовпця (LST-03): екран бачить її одразу (кеш `/meta`), сервер — за
 * `WIDTHS_SAVE_DELAY_MS` після останнього жесту. `undefined` — ширина за типом поля. `onError` —
 * збій відкладеного запису.
 */
export function saveViewWidth(
  client: QueryClient,
  view: MetaView,
  field: string,
  width: number | undefined,
  onError: () => void,
): void {
  const { id, next } = change(client, view, (prefs) => {
    const widths = { ...(prefs.widths ?? {}) };
    if (width === undefined) delete widths[field];
    else widths[field] = width;
    if (Object.keys(widths).length > 0) prefs.widths = widths;
    else delete prefs.widths;
  });
  clearTimeout(widthTimers.get(id)?.timer);
  const timer = setTimeout(() => {
    widthTimers.delete(id);
    persist(client, id, next).catch(onError);
  }, WIDTHS_SAVE_DELAY_MS);
  widthTimers.set(id, { timer, next });
}

/** Ширини, що ще чекають запису, — на сервер зараз (людина йде зі списку чи скидає шар). */
export function flushViewWidths(client: QueryClient): void {
  for (const [id, { timer, next }] of widthTimers) {
    clearTimeout(timer);
    widthTimers.delete(id);
    persist(client, id, next).catch(() => undefined);
  }
}

/**
 * Закріпити подання в перемикачі (FLT-06): останнім за порядком. Межу (`VIEW_PINNED_MAX`) перевіряє
 * екран до виклику, сервер — ще раз (`409 pinned_limit`).
 */
export async function saveViewPinned(
  client: QueryClient,
  view: MetaView,
  pinned: boolean,
): Promise<void> {
  const views = client.getQueryData<AppMeta>(platformKeys.meta)?.views ?? [];
  const last = Math.max(-1, ...views.map((v) => (v.prefs?.pinned ? (v.prefs.position ?? 0) : -1)));
  const { id, next } = change(client, view, (prefs) => {
    if (pinned) {
      prefs.pinned = true;
      prefs.position = Math.min(999, last + 1);
    } else {
      delete prefs.pinned;
      delete prefs.position;
    }
  });
  await persist(client, id, next);
}

/**
 * Записи шару одного подання — по черзі: шар пишеться цілком, тож дві швидкі зміни стовпців,
 * що обігнали одна одну в мережі, лишили б на сервері старішу (рев'ю Архітектора #819, Nit).
 * Збій попереднього запису наступного не зупиняє.
 */
const chains = new Map<string, Promise<void>>();

function inOrder(id: string, task: () => Promise<void>): Promise<void> {
  const next = (chains.get(id) ?? Promise.resolve()).catch(() => undefined).then(task);
  chains.set(id, next);
  next
    .finally(() => {
      if (chains.get(id) === next) chains.delete(id);
    })
    .catch(() => undefined);
  return next;
}

/**
 * Одноразове перенесення старого вибору стовпців з пристрою на сервер. Серверний шар уже є — він
 * головний, старий запис просто прибирається. Спроба — одна на подання за завантаження застосунку:
 * збій перечитує `/meta`, і ефект екрана інакше ганяв би `PUT` по колу. Запис тоді лишається на
 * пристрої й діє, наступне завантаження спробує знову.
 */
export async function migrateLocalColumns(client: QueryClient, view: MetaView): Promise<void> {
  const local = personalColumns(getPrefs().listColumns, view.key);
  if (local === undefined || !view.id || attempted.has(view.id)) return;
  attempted.add(view.id);
  if (view.prefs?.columns === undefined) await saveViewColumns(client, view, local);
  setPrefs({
    listColumns: withPersonalColumns(getPrefs().listColumns, view.key, undefined, LIST_COLUMNS_MAX),
  });
}

const attempted = new Set<string>();

/** Тести: кожен починає з чистого «завантаження». */
export function resetViewPrefsForTests(): void {
  attempted.clear();
  chains.clear();
  for (const { timer } of widthTimers.values()) clearTimeout(timer);
  widthTimers.clear();
}
